import { useEffect, useMemo, useState } from 'react';
import { TypedCacheEntry } from './CacheEntry';
import { usePersistentCache } from './PersistentCacheProvider';

export type CacheHookOptions<T> = {
  prefetchOnly?: boolean;
  minTtlOnAccessMs?: number;
  defaultValue?: T;
};

/**
 * Subscribes to a cache entry for the given key.
 *
 * @param key - The cache key to read and subscribe to.
 * @param prefetchOnly - If true, only metadata is read (entry.value will be
 * undefined) and metadata subscriptions are used.
 * @param minTtlOnAccessMs - If set, on mount (and whenever `key` changes), if
 * an existing entry is found for `key`, its `expiresAt` is pushed forward so
 * it will not be purged for at least this many milliseconds from now. The
 * expiration is never shortened. This only affects eviction — it does NOT
 * change `createdAt` or any staleness calculation. Because every mount pushes
 * the expiration forward, frequently mounted entries may effectively never be
 * evicted; use a separate mechanism (e.g. staleAgeMs in useCachedQuery) to
 * trigger refetches for freshness.
 */
export const usePersistentCachedEntry = <T extends unknown>(key: string, options?: CacheHookOptions<T>) => {
  const { prefetchOnly = false, minTtlOnAccessMs, defaultValue } = options || {};
  const cache = usePersistentCache<T | undefined>();
  const currentEntry = prefetchOnly ? cache.getMetadataSync(key) : cache.getSync(key);
  const [cacheEntry, setCacheEntry] = useState<TypedCacheEntry<T | undefined> | undefined>(currentEntry as TypedCacheEntry<T | undefined> | undefined);
  const [ready, setReady] = useState(false);

  useEffect(
    () => {
      // `stale` flag solves a race condition where the subscription could resolve BEFORE the initial async getEntry call.
      // In that case, the initial async getEntry call would overwrite the subscription data with undefined.
      // Concretely, this happens when:
      // 1. getEntry is called (cache miss, captures undefined at call-time)
      // 2. fetch completes → subscription fires → setCacheEntry(entry) ✓
      // 3. getEntry resolves with stale undefined → setCacheEntry(undefined) — STUCK ✗
      let stale = false;

      (prefetchOnly ? cache.getMetadata(key) : cache.getEntry(key)).then(entry => {
        // Freshen runs regardless of `stale`: even if a subscription update already
        // delivered fresh data (racing ahead of this `.then`), we still need to push
        // expiresAt forward. freshen is idempotent and only acts when the entry exists.
        if (entry && minTtlOnAccessMs != null) {
          const targetExpiresAt = Date.now() + minTtlOnAccessMs;
          if (entry.expiresAt < targetExpiresAt) cache.freshen(key, targetExpiresAt);
        }
        if (stale) return;
        setCacheEntry(entry as TypedCacheEntry<T | undefined> | undefined);
        setReady(true);
      });

      const handleSubscription = (entry: TypedCacheEntry<T | undefined>) => {
        stale = true;
        setCacheEntry(entry);
        setReady(true);
      };

      const unsubscribe = prefetchOnly
        ? cache.subscribeToMetadata(key, handleSubscription)
        : cache.subscribe(key, handleSubscription);

      return () => {
        stale = true;
        unsubscribe();
      };
    },
    [key, cache, prefetchOnly, minTtlOnAccessMs],
  );

  const defaultValueEntry = useMemo(() => {
    return {
      value: defaultValue,
      createdAt: Date.now(),
      expiresAt: Date.now(),
    };
  }, [defaultValue]);

  if (!cacheEntry && ready && defaultValue != null) {
    return defaultValueEntry;
  }

  return cacheEntry;
};

/**
 * Returns the value of the cache entry.
 * @param key - The cache key to read and subscribe to.
 * @param options - The options for the cache entry.
 *   - prefetchOnly: If true, only metadata is read (entry.value will be
 *     undefined) and metadata subscriptions are used.
 *   - minTtlOnAccessMs: If set, on mount (and whenever `key` changes), if
 *     an existing entry is found for `key`, its `expiresAt` is pushed forward so
 *     it will not be purged for at least this many milliseconds from now. The
 *     expiration is never shortened. This only affects eviction — it does NOT
 *     change `createdAt` or any staleness calculation. Because every mount pushes
 *     the expiration forward, frequently mounted entries may effectively never be evicted; use a separate mechanism (e.g. staleAgeMs in useCachedQuery) to trigger refetches for freshness.
 *   - defaultValue: The default value to return if the cache entry is not found.
 * @returns The value of the cache entry. If default Value is set, undefined can still be returned while initializing, but once the entry is confirmed found or not found, the entry or an entry containing the default value is returned.
 */
export const usePersistentCachedValue = <T extends unknown>(key: string, options?: CacheHookOptions<T>): T | undefined => {
  const cacheEntry = usePersistentCachedEntry<T>(key, options);
  return cacheEntry?.value;
};
